import React, { useState, useEffect } from "react";
import { TextField, Button, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Box, Autocomplete } from "@mui/material";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getDocs } from 'firebase/firestore';
import { FirebaseConfig } from "../../../data/firebaseConfig";
import { useFirebaseService } from "../../../data/FirebaseService";
import { toast } from "react-toastify";

export interface Person {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    domainName?: string;
    username?: string;
    externalId?: string;
    groups: string[];
}

interface AddPersonDialogProps {
    onPersonAdded?: (person: Person) => void;
    onPersonUpdated?: (person: Person) => void;
    editPerson?: Person | null;
    open?: boolean;
    onClose?: () => void;
}

export const AddPersonDialog: React.FC<AddPersonDialogProps> = ({
    onPersonAdded,
    onPersonUpdated,
    editPerson,
    open: externalOpen,
    onClose: externalOnClose
}) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState<boolean>(false);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [domainName, setDomainName] = useState("");
    const [username, setUsername] = useState("");
    const [externalId, setExternalId] = useState("");
    const [groups, setGroups] = useState<string[]>([]);
    const [availableGroups, setAvailableGroups] = useState<string[]>([]);

    const firebaseService = useFirebaseService();
    const functions = getFunctions(FirebaseConfig.app);
    const createPerson = httpsCallable(functions, "createPerson");
    const updatePerson = httpsCallable(functions, "updatePerson");

    const isEditing = !!editPerson;
    const open = externalOpen !== undefined ? externalOpen : internalOpen;

    // Populate form when editing
    useEffect(() => {
        if (editPerson) {
            setFirstName(editPerson.firstName || "");
            setLastName(editPerson.lastName || "");
            setEmail(editPerson.email || "");
            setDomainName(editPerson.domainName || "");
            setUsername(editPerson.username || "");
            setExternalId(editPerson.externalId || "");
            setGroups(editPerson.groups || []);
        }
    }, [editPerson]);

    // Fetch available groups when dialog opens
    useEffect(() => {
        if (open && firebaseService) {
            const fetchGroups = async () => {
                try {
                    const groupsCollection = firebaseService.getCollection('groups');
                    const groupsSnapshot = await getDocs(groupsCollection);
                    const groupsList = groupsSnapshot.docs.map(doc => doc.data().name);
                    setAvailableGroups(groupsList);
                } catch (error) {
                    console.error("Error fetching groups:", error);
                }
            };

            fetchGroups();
        }
    }, [open, firebaseService]);

    const handleGroupsChange = (event: any, newValue: string[]) => {
        setGroups(newValue);
    };

    const resetForm = () => {
        setFirstName("");
        setLastName("");
        setEmail("");
        setDomainName("");
        setUsername("");
        setExternalId("");
        setGroups([]);
        setMessage("");
    };

    const handleSubmit = async () => {
        setLoading(true);
        setMessage("");

        if (!firstName || !lastName || !email) {
            setMessage("First name, last name, and email are required.");
            setLoading(false);
            return;
        }

        try {
            const personData = {
                firstName,
                lastName,
                email,
                domainName: domainName || "",
                username: username || "",
                externalId: externalId || "",
                groups
            };

            if (isEditing) {
                // Update existing person
                const result = await updatePerson({
                    personId: editPerson!.id,
                    ...personData
                });

                toast.success("Person updated successfully!");

                const updatedPerson: Person = {
                    id: editPerson!.id,
                    ...personData
                };

                onPersonUpdated?.(updatedPerson);
            } else {
                // Create new person
                const result = await createPerson(personData);

                toast.success("Person added successfully!");

                const newPerson: Person = {
                    id: (result.data as { personId: string }).personId || Date.now().toString(),
                    ...personData
                };

                onPersonAdded?.(newPerson);
            }

            handleClose();
            resetForm();
        } catch (error) {
            console.error(`Error ${isEditing ? 'updating' : 'creating'} person:`, error);
            setMessage(`Failed to ${isEditing ? 'update' : 'add'} person. Please try again.`);
        }
        setLoading(false);
    };

    const handleClose = () => {
        if (externalOnClose) {
            externalOnClose();
        } else {
            setInternalOpen(false);
        }
        resetForm();
    };

    return (
        <div>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{isEditing ? 'Edit Person' : 'Add New Person'}</DialogTitle>
                <DialogContent>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <>
                            <TextField
                                label="First Name *"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                fullWidth
                                margin="normal"
                            />
                            <TextField
                                label="Last Name *"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                fullWidth
                                margin="normal"
                            />
                            <TextField
                                label="Email *"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                                margin="normal"
                            />
                            <TextField
                                label="Domain Name"
                                value={domainName}
                                onChange={(e) => setDomainName(e.target.value)}
                                fullWidth
                                margin="normal"
                            />
                            <TextField
                                label="Username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                fullWidth
                                margin="normal"
                            />
                            <TextField
                                label="External ID"
                                value={externalId}
                                onChange={(e) => setExternalId(e.target.value)}
                                fullWidth
                                margin="normal"
                            />
                            <Autocomplete
                                multiple
                                options={availableGroups}
                                value={groups}
                                onChange={handleGroupsChange}
                                renderTags={(value: readonly string[], getTagProps) =>
                                    value.map((option: string, index: number) => (
                                        <Chip
                                            variant="outlined"
                                            label={option}
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Groups"
                                        placeholder={groups.length === 0 ? "Select groups..." : ""}
                                        margin="normal"
                                        fullWidth
                                    />
                                )}
                                sx={{ mt: 2 }}
                            />
                        </>
                    )}

                    {message && (
                        <Typography variant="body1" color="error" style={{ marginTop: 16 }}>
                            {message}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={loading} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={loading} color="primary">
                        {isEditing ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};